import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../theme';

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  dark?: boolean;
  required?: boolean;
}

const SelectContainer = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
`;

const Label = styled.label<{ required?: boolean; dark?: boolean }>`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${({ dark }) => dark ? theme.colors.light.text.primary : theme.colors.white};
  margin-bottom: ${theme.spacing[1]};

  ${({ required }) =>
    required &&
    css`
      &::after {
        content: ' *';
        color: ${theme.colors.accent.danger};
      }
    `}
`;

const StyledSelect = styled.select<{
  inputSize?: 'sm' | 'md' | 'lg';
  error?: boolean;
  disabled?: boolean;
  dark?: boolean;
}>`
  width: 100%;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.base};
  color: ${({ dark }) => dark ? theme.colors.light.text.primary : theme.colors.white};
  background-color: ${({ dark }) => dark ? theme.colors.white : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${({ error, dark }) =>
    error ? theme.colors.accent.danger : (dark ? theme.colors.light.border.medium : 'rgba(255, 255, 255, 0.2)')};
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease-in-out;
  backdrop-filter: ${({ dark }) => (dark ? 'none' : 'blur(10px)')};
  box-shadow: ${({ dark }) => (dark ? theme.shadows.sm : 'none')};
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right ${theme.spacing[3]} center;
  background-size: 1.5em;

  ${({ inputSize = 'md' }) => {
    switch (inputSize) {
      case 'sm':
        return css`
          padding: ${theme.spacing[2]} ${theme.spacing[10]} ${theme.spacing[2]} ${theme.spacing[3]};
          font-size: ${theme.typography.fontSize.sm};
          min-height: 32px;
        `;
      case 'lg':
        return css`
          padding: ${theme.spacing[4]} ${theme.spacing[10]} ${theme.spacing[4]} ${theme.spacing[4]};
          font-size: ${theme.typography.fontSize.lg};
          min-height: 48px;
        `;
      default:
        return css`
          padding: ${theme.spacing[3]} ${theme.spacing[10]} ${theme.spacing[3]} ${theme.spacing[4]};
          font-size: ${theme.typography.fontSize.base};
          min-height: 40px;
        `;
    }
  }}

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${theme.colors.light.background.tertiary};
      color: ${theme.colors.light.text.secondary};
      cursor: not-allowed;
    `}

  &:focus {
    outline: none;
    border-color: ${({ error }) => (error ? theme.colors.accent.danger : theme.colors.primary[500])};
    box-shadow: 0 0 0 3px
      ${({ error }) =>
        error ? `${theme.colors.accent.danger}20` : `${theme.colors.primary[500]}20`};
    background-color: ${({ dark }) => dark ? theme.colors.white : 'rgba(255, 255, 255, 0.15)'};
  }

  &:hover:not(:disabled) {
    border-color: ${({ error, dark }) => (error ? theme.colors.accent.danger : (dark ? theme.colors.light.border.dark : 'rgba(255, 255, 255, 0.3)'))};
    background-color: ${({ dark }) => dark ? theme.colors.white : 'rgba(255, 255, 255, 0.12)'};
  }

  option {
    color: ${theme.colors.light.text.primary};
    background-color: ${theme.colors.white};
  }
`;

const ErrorText = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.accent.danger};
  margin-top: ${theme.spacing[1]};
`;

const HelperText = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.7);
  margin-top: ${theme.spacing[1]};
`;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(({
  label,
  error,
  helperText,
  size = 'md',
  fullWidth = false,
  required = false,
  dark = false,
  children,
  ...props
}, ref) => {
  const selectId = props.id || props.name;

  return (
    <SelectContainer fullWidth={fullWidth}>
      {label && (
        <Label htmlFor={selectId} required={required} dark={dark}>
          {label}
        </Label>
      )}
      <StyledSelect
        {...props}
        ref={ref}
        id={selectId}
        inputSize={size}
        error={!!error}
        dark={dark}
        aria-invalid={!!error}
      >
        {children}
      </StyledSelect>
      {error && (
        <ErrorText role="alert">
          {error}
        </ErrorText>
      )}
      {helperText && !error && (
        <HelperText>
          {helperText}
        </HelperText>
      )}
    </SelectContainer>
  );
});

Select.displayName = 'Select';
